import React, { FC, useState } from 'react';
import { enumToComboOptionTransformer, NumericInput, StimulsoftViewer, } from '../../../core/component';
import { Button, Checkbox, Col, DatePicker, Form, Modal, Row, Select, Space } from 'antd';
import { translate } from '../../../core';
import { InventoryStoneManagementRequest } from '../../reports/sales-by-vendor/type/stone-inventory-management';
import { gql, useQuery } from '@apollo/client';
import { StoneShape } from '../type'
import { stoneClarityItems, stoneColors } from '../model-mapper/item-combe-mapper'
import { VendorSelect } from '../../contact/component/VendorSelect'
import { Dayjs } from 'dayjs'
import { dateFormatSlashed } from '../../../core/until'
import { FormEntryField } from '../../../core/type'

type InventoryStoneManagementReportForm = Omit<InventoryStoneManagementRequest, 'dateRange'> & {
  dateRange: [Dayjs, Dayjs]
}

const AverageSize: FC<FormEntryField<InventoryStoneManagementRequest['averageSizeRange']>> = ({value, onChange}) =>  (
  <Space.Compact>
    <NumericInput
      value={value?.min as number}
      onChange={min => onChange!({
        min: min as number,
        max: value?.max as number,
      })}
      placeholder={translate('from')}
    />
    <NumericInput
      value={value?.max as number}
      onChange={max => onChange!({
        min: value?.min as number,
        max: max as number,
      })}
      placeholder={translate('to')}
    />
  </Space.Compact>
)

export const InventoryStoneManagementReport = () => {
  const [form] = Form.useForm<InventoryStoneManagementReportForm>();
  const [request, setRequest] = useState<InventoryStoneManagementRequest>();
  const {data} = useQuery<{ itemStoneNames: string[] }>(gql`
      query GetItemStoneNames {
          itemStoneNames
      }
  `);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false)

  const submit = (data: InventoryStoneManagementReportForm) => {
    const [min, max] = (data.dateRange ?? [])
    setRequest({
      ...data,
      dateRange: {
        min: min ? min.format(dateFormatSlashed) : null,
        max: max ? max.format(dateFormatSlashed) : null,
      },
    })
    setConfirmModalVisible(false)
  }

  return (
    <>
      <Button
        type='primary'
        onClick={() => setConfirmModalVisible(true)}
        className='btn-primary-gradient'
      >
        {translate('new_report')}
      </Button>
      <div className='print-hidden'>
        <StimulsoftViewer
          name='inventory-stone-management'
          request={request}
          skip={!request}
        />
      </div>
      <Modal
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        width='40%'
        footer={<Space>
          <Button
            onClick={() => setConfirmModalVisible(false)}
          >
            {translate('cancel')}
          </Button>

          <Button
            onClick={() => form.resetFields()}
          >
            {translate('clear')}
          </Button>

          <Button
            type='primary'
            onClick={form.submit}
          >
            {translate('get_report')}
          </Button>

        </Space>}
      >
        <Form
          initialValues={{inStock: true, showCertificationNo: true}}
          layout='vertical'
          form={form}
          onFinish={submit}
          className='d-flex flex-column gap-2'
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('stone', 'name')}
                name='name'
              >
                <Select
                  allowClear
                  mode='multiple'
                  options={data?.itemStoneNames.map(name => ({
                    key: name,
                    value: name,
                    label: name,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('supplier')}
                name='supplier'
              >
                <VendorSelect
                  mode='multiple'
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('shape')}
                name='shape'
              >
                <Select
                  allowClear
                  mode='multiple'
                  options={enumToComboOptionTransformer(StoneShape)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('average_weight', '(ct)')}
                name='averageSizeRange'
              >
                <AverageSize/>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('color')}
                name='color'
              >
                <Select
                  allowClear
                  mode='multiple'
                  options={stoneColors}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('clarity')}
                name='clarity'
              >
                <Select
                  allowClear
                  mode='multiple'
                  options={stoneClarityItems}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('date_range')}
                name='dateRange'
              >
                <DatePicker.RangePicker/>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                className='mb-1'
                label={translate('certification', 'status')}
                name='certificationStatus'
              >
                <Select
                  allowClear
                  options={[
                    {
                      value: 'All',
                      label: 'All',
                    },
                    {
                      value: 'OnlyCertified',
                      label: 'Only Certified',
                    },
                    {
                      value: 'NoCert',
                      label: 'No Cert',
                    }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Space direction='vertical'>
                <Form.Item
                  noStyle
                  name='showCertificationNo'
                  valuePropName='checked'
                >
                  <Checkbox>{translate('show', 'certificate', 'number_abbr')}</Checkbox>
                </Form.Item>

                <Form.Item
                  noStyle
                  name='showCost'
                  valuePropName='checked'
                >
                  <Checkbox>{translate('show', 'cost')}</Checkbox>
                </Form.Item>

                <Form.Item
                  name='showArchives'
                  valuePropName='checked'
                  noStyle
                >
                  <Checkbox>{translate('show','archives')}</Checkbox>
                </Form.Item>
              </Space>
            </Col>
            <Col span={12}>
              <Form.Item
                name='inStock'
                valuePropName='checked'
                noStyle
              >
                <Checkbox>{translate('in_stock_only')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
